import type React from 'react';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import type { Chart as ChartJS } from 'chart.js';

import { analytics } from '@/analytics';
import CopyFill from '@/assets/icons/copy_fill.svg?component';
import Download2Line from '@/assets/icons/download_2_line.svg?component';
import FullscreenExitLine from '@/assets/icons/fullscreen_exit_line.svg?component';
import FullscreenLine from '@/assets/icons/fullscreen_line.svg?component';
import More2Fill from '@/assets/icons/more_2_fill.svg?component';
import ZoomOutLine from '@/assets/icons/zoom_out_line.svg?component';
import { SvgIcon } from '@/components/atoms/SvgIcon/SvgIcon';
import { chartImageBlob } from '@/components/charts/exportChartImage';
import { useChartTheme } from '@/components/charts/theme';
import { Menu, MenuContent, MenuItem, MenuTrigger } from '@/components/molecules/Menu';
import { ToastError, ToastSuccess } from '@/components/molecules/ToastTemplate/ToastTemplate';
import { saveBlob } from '@/utils/saveBlob';

import { downloadChartCsv, fileDate } from './chartFiles';

import styles from './ChartActionsMenu.module.scss';

export interface ChartActionsMenuProps {
    chartRef: React.RefObject<ChartJS<'line' | 'bar'> | null>;
    chartName?: string;
    isFullScreen: boolean;
    onFullScreenToggle: () => void;
    disabled?: boolean;
    /**
     * Defaults to `true`. `false` leaves out "Download data as CSV", on a page whose own Export
     * already downloads the data behind its charts.
     */
    csvExport?: boolean;
}

export default function ChartActionsMenu(props: ChartActionsMenuProps) {
    const {
        chartRef,
        chartName = 'chart',
        isFullScreen,
        onFullScreenToggle,
        disabled = false,
        csvExport = true,
    } = props;
    const { t } = useTranslation();
    // Exports are filled with the chart's surface in the theme on screen (D8), so the image
    // looks like the chart did rather than its lines on a transparent background.
    const { surface } = useChartTheme();

    const canCopyImage =
        typeof window.ClipboardItem !== 'undefined' &&
        typeof navigator.clipboard?.write === 'function';

    const handleCopyAsPng = useCallback(() => {
        const chart = chartRef.current;
        if (!chart) {
            console.warn('No chart to copy');
            return;
        }

        const copyFailed = (): void => {
            ToastError(t('molecules.copyAsImage.error'), 'copy-chart-error');
        };

        // Safari drops the click's user activation at the first await, so the item is built
        // now and handed the image while it is still being drawn, as useCopyElementAsImage does.
        const png = new Promise<Blob>((resolve, reject) => {
            chartImageBlob(chart.canvas, surface, (blob) => {
                if (blob) resolve(blob);
                else reject(new Error('The chart could not be drawn as an image'));
            });
        });

        try {
            navigator.clipboard
                .write([new window.ClipboardItem({ 'image/png': png })])
                .then(() => {
                    analytics.exportTriggered({ exportType: chartName, fileFormat: 'png' });
                    ToastSuccess(t('molecules.copyAsImage.success'), 'copy-as-image-success');
                })
                .catch(copyFailed);
        } catch {
            copyFailed();
        }
    }, [chartRef, chartName, surface, t]);

    const handleResetZoom = useCallback(() => {
        if (!chartRef.current) {
            console.warn('No chart to reset zoom');
            return;
        }

        chartRef.current.resetZoom();
    }, [chartRef]);

    const handleDownloadAsPng = useCallback(() => {
        if (!chartRef.current) {
            console.warn('No chart to download');
            return;
        }

        const fileName = `${chartName}-${fileDate()}.png`;

        analytics.exportTriggered({
            exportType: chartName,
            fileFormat: 'png',
        });

        chartImageBlob(chartRef.current.canvas, surface, (blob) => {
            if (blob) {
                saveBlob(blob, fileName);
            }
        });
    }, [chartRef, chartName, surface]);

    const handleDownloadAsCsv = useCallback(() => {
        const chart = chartRef.current;
        if (!chart) {
            console.warn('No chart to download');
            return;
        }

        downloadChartCsv(chart, chartName, {
            day: t('molecules.chartActionsMenu.csvColumn.day'),
            week: t('molecules.chartActionsMenu.csvColumn.week'),
            hour: t('molecules.chartActionsMenu.csvColumn.hour'),
            category: t('molecules.chartActionsMenu.csvColumn.category'),
        });
    }, [chartRef, chartName, t]);

    const handleFullScreenToggle = useCallback(() => {
        onFullScreenToggle();
    }, [onFullScreenToggle]);

    return (
        <div className={styles.chartActionsMenu}>
            <Menu disabled={disabled}>
                <MenuTrigger
                    variant="secondary"
                    iconOnly
                    aria-label={t('molecules.chartActionsMenu.actions')}
                    disabled={disabled}
                    className={styles.toggleButton}
                    icon={<SvgIcon icon={More2Fill} color="currentColor" />}
                />

                <MenuContent align="end" width="wide">
                    {canCopyImage && (
                        <MenuItem
                            onClick={handleCopyAsPng}
                            icon={<SvgIcon icon={CopyFill} color="currentColor" />}>
                            {t('molecules.chartActionsMenu.copyAsPng')}
                        </MenuItem>
                    )}

                    <MenuItem
                        onClick={handleResetZoom}
                        icon={<SvgIcon icon={ZoomOutLine} color="currentColor" />}>
                        {t('molecules.chartActionsMenu.resetZoom')}
                    </MenuItem>

                    <MenuItem
                        onClick={handleDownloadAsPng}
                        icon={<SvgIcon icon={Download2Line} color="currentColor" />}>
                        {t('molecules.chartActionsMenu.downloadAsPng')}
                    </MenuItem>

                    {csvExport && (
                        <MenuItem
                            onClick={handleDownloadAsCsv}
                            icon={<SvgIcon icon={Download2Line} color="currentColor" />}>
                            {t('molecules.chartActionsMenu.downloadAsCsv')}
                        </MenuItem>
                    )}

                    <MenuItem
                        onClick={handleFullScreenToggle}
                        icon={
                            <SvgIcon
                                color="currentColor"
                                icon={isFullScreen ? FullscreenExitLine : FullscreenLine}
                            />
                        }>
                        {isFullScreen
                            ? t('molecules.chartActionsMenu.exitFullscreen')
                            : t('molecules.chartActionsMenu.fullscreen')}
                    </MenuItem>
                </MenuContent>
            </Menu>
        </div>
    );
}
